/* Genre-based popular TV shows grid powered by IMDb API. */

import { useState, useCallback } from "react";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Loader2, Tv2, Star } from "lucide-react";
import type { PopularTvShow } from "@/types/tvShowDiscover";

const GENRE_OPTIONS = [
  { value: "drama", label: "Drama" },
  { value: "comedy", label: "Comedy" },
  { value: "action", label: "Action" },
  { value: "crime", label: "Crime" },
  { value: "scifi", label: "Sci-Fi" },
  { value: "mystery", label: "Mystery" },
  { value: "animation", label: "Animation" },
  { value: "documentary", label: "Documentary" },
  { value: "thriller", label: "Thriller" },
  { value: "fantasy", label: "Fantasy" },
  { value: "horror", label: "Horror" },
  { value: "romance", label: "Romance" },
] as const;

interface TvShowBrowsePopularProps {
  onSelectShow: (show: PopularTvShow) => void;
}

export function TvShowBrowsePopular({ onSelectShow }: TvShowBrowsePopularProps) {
  const [shows, setShows] = useState<PopularTvShow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeGenre, setActiveGenre] = useState<string | null>(null);

  const fetchPopular = useAction(api.tvShowDiscover.fetchPopularShows);

  const loadGenre = useCallback(
    async (genre: string) => {
      setActiveGenre(genre);
      setIsLoading(true);
      try {
        const data = await fetchPopular({ genre });
        setShows(data);
      } catch {
        setShows([]);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPopular],
  );

  function handleGenreClick(genre: string) {
    if (activeGenre === genre) return;
    loadGenre(genre);
  }

  return (
    <div className="space-y-3">
      {/* Genre pill filter bar */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1">
        {GENRE_OPTIONS.map((g) => (
          <button
            key={g.value}
            type="button"
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs transition-colors ${
              activeGenre === g.value
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
            onClick={() => handleGenreClick(g.value)}
          >
            {g.label}
          </button>
        ))}
      </div>

      {/* Prompt to select genre */}
      {!activeGenre && !isLoading && (
        <p className="text-xs text-muted-foreground text-center py-3">
          Select a genre to browse popular TV shows.
        </p>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center gap-2 py-4 justify-center">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            Loading popular shows...
          </span>
        </div>
      )}

      {/* Shows grid */}
      {!isLoading && shows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
          {shows.map((show) => (
            <button
              key={show.imdbId}
              type="button"
              className="flex flex-col items-center gap-1 p-2 rounded-md border border-border cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSelectShow(show)}
            >
              {show.poster ? (
                <img
                  src={show.poster}
                  alt={show.title}
                  className="w-16 h-24 rounded-sm object-cover bg-muted"
                />
              ) : (
                <div className="w-16 h-24 rounded-sm bg-muted flex items-center justify-center">
                  <Tv2 className="size-5 text-muted-foreground/50" />
                </div>
              )}
              <span className="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                {show.title}
              </span>
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground flex-wrap justify-center">
                <span>{show.year}</span>
                {show.totalSeasons && (
                  <>
                    <span>·</span>
                    <span>{show.totalSeasons}S</span>
                  </>
                )}
                {show.rating > 0 && (
                  <>
                    <span>·</span>
                    <Star className="size-2.5 fill-current text-yellow-500" />
                    <span>{show.rating}</span>
                  </>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && activeGenre && shows.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-3">
          No results found for this genre.
        </p>
      )}
    </div>
  );
}
